import { useEffect, useState } from 'react'
import { Badge, Group, Text } from '@mantine/core'
import type { ShiftBadgeStatus, ShiftStatusBadgeRow } from '@/types/api'

/** Метаданные плашки статуса смены: подпись и цвет. */
const STATUS_META: Record<ShiftBadgeStatus, { label: string; color: string }> = {
  on_shift: { label: 'На смене', color: 'green' },
  on_break: { label: 'На перерыве', color: 'yellow' },
  coming_soon: { label: 'Скоро буду', color: 'blue' },
  delayed: { label: 'Задерживаюсь', color: 'orange' },
  no_shift: { label: 'Нет смены', color: 'gray.6' },
}

/** Периодический ре-рендер (для «живых» минут в статусах «на перерыве»/«задерживаюсь»). */
function useMinuteTick(active: boolean) {
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000)
    return () => window.clearInterval(id)
  }, [active])
}

/** ISO-мгновение → «ЧЧ:ММ» в заданной таймзоне (плановые времена — МСК). */
function timeInTz(iso: string | null | undefined, tz: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    }).format(d)
  } catch {
    return new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d)
  }
}

/** Сколько целых минут прошло от ISO-мгновения до сейчас (>= 0). */
function minutesSince(iso: string | null | undefined): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.max(0, Math.floor((Date.now() - t) / 60_000))
}

/** «YYYY-MM-DD» → «дд.мм.гг». */
function formatDateDdMmYy(isoDate: string | null | undefined): string {
  if (!isoDate) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate)
  if (!m) return ''
  return `${m[3]}.${m[2]}.${m[1].slice(2)}`
}

export function OperatorShiftStatusBadge({
  badge,
  scheduleTimezone = 'Europe/Moscow',
}: {
  badge: ShiftStatusBadgeRow | undefined
  scheduleTimezone?: string
}) {
  const needsTick =
    !!badge && (badge.status === 'on_break' || badge.status === 'delayed')
  useMinuteTick(needsTick)

  if (!badge) return null
  const meta = STATUS_META[badge.status] ?? STATUS_META.no_shift
  const ps = badge.planned_start_local || ''
  const pe = badge.planned_end_local || ''
  const label = (badge.planned_intervals_label || '').trim()

  let detail = ''
  switch (badge.status) {
    case 'on_shift': {
      if (ps && pe) detail = `с ${ps} до ${pe}`
      else if (label) detail = label
      else if (badge.shift_started_at)
        detail = `с ${timeInTz(badge.shift_started_at, scheduleTimezone)}`
      break
    }
    case 'on_break': {
      const parts: string[] = []
      const brk = timeInTz(badge.break_started_at, scheduleTimezone)
      if (brk) parts.push(`с ${brk}`)
      const elapsed = minutesSince(badge.break_started_at)
      if (elapsed != null) parts.push(`Прошло ${elapsed} мин`)
      if (ps && pe) parts.push(`Смена с ${ps} до ${pe}`)
      else if (label) parts.push(`Смена ${label}`)
      detail = parts.join(' | ')
      break
    }
    case 'coming_soon': {
      if (ps) detail = `Смена по плану ${ps}`
      else if (label) detail = `Смена по плану ${label}`
      break
    }
    case 'delayed': {
      const parts: string[] = []
      if (ps && pe) parts.push(`Смена по плану ${ps}–${pe}`)
      else if (ps) parts.push(`Смена по плану ${ps}`)
      else if (label) parts.push(`Смена по плану ${label}`)
      // Бэкенд выбирает точку отсчёта таймера в зависимости от причины задержки
      // (плановое начало / конец макс. перерыва / момент вылета по бездействию).
      // Fallback на planned_start_at сохраняет обратную совместимость.
      const late = minutesSince(badge.delayed_since || badge.planned_start_at)
      if (late != null) parts.push(`Задерживаюсь на ${late} мин`)
      detail = parts.join(' | ')
      break
    }
    case 'no_shift': {
      const nd = formatDateDdMmYy(badge.next_shift_date)
      if (nd) detail = `Ближайшая смена ${nd}`
      break
    }
  }

  return (
    <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
      <Badge
        size="sm"
        variant={badge.status === 'no_shift' ? 'filled' : 'light'}
        color={meta.color}
        style={{ flexShrink: 0, textTransform: 'none' }}
      >
        {meta.label}
      </Badge>
      {detail ? (
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {detail}
        </Text>
      ) : null}
    </Group>
  )
}
